import type { PatchEntry, PatchesData } from "$lib/types";

/**
 * Oldest patch we have stats/map history for. Patches before this exist in
 * Blizzard's notes (patches.json) but have no data to reconstruct, so the patch
 * selectors hide them.
 */
export const EARLIEST_PATCH_WITH_DATA = "2026-02-24";

/**
 * Central source for every patch **selector** in the frontend (hero, map, tier
 * list, hero index). Fetches balance patches (newest-first) from
 * `/data/patches.json`, filtered to those we actually have data for. Keeping the
 * cutoff here means it is defined in exactly one place. Returns `[]` on failure.
 *
 * Note: patch-note archive pages (`/patches`, sitemap) intentionally list ALL
 * patches and must NOT use this.
 */
export async function fetchSelectablePatches(fetchFn: typeof fetch): Promise<PatchEntry[]> {
    try {
        const res = await fetchFn("/data/patches.json");
        if (!res.ok) return [];
        const patches: PatchesData = await res.json();
        return patches.patches.filter((p) => p.hasBalanceChanges && p.date >= EARLIEST_PATCH_WITH_DATA);
    } catch {
        return [];
    }
}

/** Convenience wrapper around {@link fetchSelectablePatches}: just the dates. */
export async function fetchBalancePatchDates(fetchFn: typeof fetch): Promise<string[]> {
    return (await fetchSelectablePatches(fetchFn)).map((p) => p.date);
}
